import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Tooltip,
  Skeleton,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useCitationStyles } from "../../hooks/useCitation";
import { useCitationData } from "../../hooks/useCitation";
import { TextField, Stack, MenuItem, Popover, FormControlLabel, Checkbox } from "@mui/material";
import useCitationItemSwitch from "../../hooks/useCitation/use-citation-item-switch.hook";
import useCitationGenerate from "../../hooks/useCitation/use-citation-generate.hook";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { getRegularDate } from "utils/time.util";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import _ from "lodash";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { logEvent } from "services/amplitude.service";
import CloseIcon from "@mui/icons-material/Close";

import { itemTypes as localItemTypes, resolveItemTypeKey } from "data/item-types";
import {
  DD_OPTIONS,
  MM_OPTIONS,
  SINGLE_MONTH_OPTIONS,
  MONTH_NAME_TO_NUMBER,
} from "../../data/citation-date";

const NOODLETOOLS_ACCESS_KEY = process.env.REACT_APP_NOODLETOOLS_ACCESS_KEY;
const NOODLETOOLS_CLIENT_KEY = process.env.REACT_APP_NOODLETOOLS_CLIENT_KEY;
const CITATION_YEAR_MAX_LENGTH = 4;
const CITATION_YEAR_ND_VALUE = "n.d.";
const CITATION_YEAR_NONE_VALUE = "NONE";
const CITATION_DATE_LABELS = {
  day: "Day (DD)",
  month: "Month (MM)",
  year: "Year (YYYY)",
};
const MONTH_NUMBER_TO_NAME = Object.entries(MONTH_NAME_TO_NUMBER).reduce(
  (result, [monthName, monthNumber]) => ({
    ...result,
    [monthNumber]: monthName,
  }),
  {}
);
const CITATION_DATE_NONE_VALUE = "";

const formatCitationDateValue = (value) => {
  if (!value) {
    return "";
  }

  if (typeof value === "string") {
    return value.trim();
  }

  return getRegularDate(value);
};

const parseCitationDateParts = (value) => {
  if (!value) return { day: "", month: "", year: "" };
  if (typeof value === "object") {
    const formattedDate = getRegularDate(value);
    if (!formattedDate) return { day: "", month: "", year: "" };
    return parseCitationDateParts(formattedDate);
  }

  const trimmed = String(value).trim();
  if (!trimmed) return { day: "", month: "", year: "" };

  const mmddMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (mmddMatch) {
    const [, m, d, y] = mmddMatch;
    return {
      day: String(Number(d)),
      month: MONTH_NUMBER_TO_NAME[m.padStart(2, "0")] || "",
      year: y,
    };
  }

  const parts = trimmed.split(/\s+/);
  let day = "";
  let month = "";
  let year = "";

  if (parts.length > 0) {
    const last = parts[parts.length - 1];
    const isLastMonth = MM_OPTIONS.includes(last) || SINGLE_MONTH_OPTIONS.includes(last);

    // If it's a month name, it's only a year if it's the ONLY part and not a month name, 
    // or if we have multiple parts and the last one is NOT a month name.
    if (!isLastMonth || parts.length > 1) {
      if (!isLastMonth || !/^[A-Za-z]+$/.test(last)) {
        year = parts.pop();
      }
    }
  }

  if (parts.length > 0) {
    if (DD_OPTIONS.includes(parts[0]) && parts[0] !== CITATION_DATE_NONE_VALUE) {
      day = parts.shift();
    }
    month = parts.join(" ");
  }

  return { day, month, year };
};

const buildCitationDateValue = ({ day, month, year, noDate }) => {
  if (noDate || year === "n.d.") {
    return "n.d.";
  }

  const isSingleMonth = SINGLE_MONTH_OPTIONS.includes(month);
  let yearToBuild = year;
  if (year === CITATION_YEAR_NONE_VALUE) yearToBuild = "";

  return [isSingleMonth ? day : "", month, yearToBuild]
    .filter(Boolean)
    .join(" ")
    .trim();
};


const isCitationYearValueValid = (year) =>
  year === "" || /^\d{4}$/.test(year) || year === "n.d.";

const isStructuredCitationDate = (dateValue) => {
  if (!dateValue) {
    return false;
  }

  const trimmedDateValue = String(dateValue).trim();
  if (!trimmedDateValue || !/\d{4}/.test(trimmedDateValue)) {
    return false;
  }

  const parts = parseCitationDateParts(trimmedDateValue);
  if (!parts.month || !parts.year) {
    return false;
  }

  if (!SINGLE_MONTH_OPTIONS.includes(parts.month)) {
    return false;
  }

  if (!parts.day) {
    return false;
  }

  const parsedDate = new Date(trimmedDateValue);
  return !Number.isNaN(parsedDate.getTime()) && parsedDate.getFullYear() > 1900;
};

const normalizeCitationYearValue = (value) => {
  const normalizedValue = value.toLowerCase().trim();
  const numericOnly = normalizedValue.replace(/[^0-9]/g, "");
  if (numericOnly.length <= 4 && /^\d*$/.test(numericOnly)) {
    return numericOnly;
  }
  return null;
};


const Citation = ({
  citation,
  setCitation,
  articleData,
  setArticleData,
  selectedStyle,
  setSelectedStyle,
  authors,
  setAuthors,
  selectedBookmarkOption,
  citationLoaded,
  setCitationLoaded,
  setOriginalArticleData,
  originalArticleData,
  setDisable,
  originalAuthors,
  setOriginalAuthors,
  disable,
}) => {
  const [currentItemType, setCurrentItemType] = useState("webpage");
  const [loading, setLoading] = useState(true);
  const [additionalData, setAdditionalData] = useState({});
  const [editingValue, setEditingValue] = useState("");
  const [editingField, setEditingField] = useState(null);
  const [date, setDate] = useState(null);
  const [accessDate, setAccessDate] = useState(null);
  const [citationDay, setCitationDay] = useState("");
  const [citationMonth, setCitationMonth] = useState("");
  const [citationYear, setCitationYear] = useState("");
  const [citationYearError, setCitationYearError] = useState(false);
  const [authorIndex, setAuthorIndex] = useState(0);
  const [styleChanged, setStyleChanged] = useState(false);
  const [noDate, setNoDate] = useState(false);
  const hasHydratedInitialCitationRef = React.useRef(false);
  const isDateIncomplete =
    !noDate &&
    ((Boolean(citationDay) && (!citationMonth || !citationYear)) ||
      (Boolean(citationMonth) && !citationYear) ||
      (Boolean(citationMonth) && !SINGLE_MONTH_OPTIONS.includes(citationMonth) && Boolean(citationDay)));

  const formRef = React.useRef();
  const refWorksFormRef = React.useRef();
  const citationYearInputRef = React.useRef(null);

  const { itemTypes, summaryInfoData, isCitationAvailable, isReadOnlyView } = useSelector(
    (state) => state.data
  );

  // The Item Type select only shows a selection when currentItemType exactly
  // matches an option key, but the keys come from the backend and may differ
  // in casing from values set elsewhere (e.g. "webpage" vs "webPage").
  useEffect(() => {
    if (!itemTypes?.length || !currentItemType) return;
    const resolved = resolveItemTypeKey(currentItemType, itemTypes);
    if (resolved !== currentItemType) setCurrentItemType(resolved);
  }, [itemTypes, currentItemType]);

  const { citationStyles } = useCitationStyles(
    selectedStyle,
    setSelectedStyle,
    selectedBookmarkOption,
    citationLoaded
  );
  const {
    baseFields,
    fields,
    requiredFields,
    softRequirementFields,
    setRequiredFields,
    setFields,
    setBaseFields,
    resetArticleData,
    resetAdditionalData,
    setSoftRequirementFields,
  } = useCitationData(
    setLoading,
    setArticleData,
    setCurrentItemType,
    setAuthors,
    setDate,
    setAccessDate,
    setCitation,
    setSelectedStyle,
    articleData,
    additionalData,
    setAdditionalData,
    selectedBookmarkOption,
    citationLoaded,
    setCitationLoaded,
    setOriginalArticleData,
    setOriginalAuthors,
    selectedStyle,
    currentItemType
  );

  const { handleItemChange } = useCitationItemSwitch(
    setLoading,
    baseFields,
    setBaseFields,
    additionalData,
    setAdditionalData,
    articleData,
    setArticleData,
    fields,
    setFields,
    setCurrentItemType,
    setRequiredFields,
    resetArticleData,
    resetAdditionalData,
    selectedStyle,
    setSoftRequirementFields
  );

  const { fetchCitation, tooltipText, handleCopyClick, handleMouseLeave } =
    useCitationGenerate(
      requiredFields,
      articleData,
      setLoading,
      citation,
      setCitation,
      currentItemType,
      selectedStyle,
      authors,
      setDisable,
      selectedBookmarkOption,
    );

  const buildRISData = () => {
    const lines = [];
    const risTypeMap = {
      blogPost: "BLOG",
      webpage: "ELEC",
      artwork: "ART",
      attachment: "GEN",
      audioRecording: "SOUND",
      bill: "BILL",
      book: "BOOK",
      bookSection: "CHAP",
      case: "CASE",
      computerProgram: "COMP",
      conferencePaper: "CPAPER",
      dataset: "DATA",
      dictionaryEntry: "DICT",
      document: "GEN",
      email: "PCOMM",
      encyclopediaArticle: "ENCYC",
      film: "MPCT",
      forumPost: "ICOMM",
      hearing: "HEAR",
      instantMessage: "ICOMM",
      interview: "GEN",
      journalArticle: "JOUR",
      letter: "PAMP",
      magazineArticle: "MGZN",
      manuscript: "MANSCPT",
      map: "MAP",
      newspaperArticle: "NEWS",
      patent: "PAT",
      podcast: "POD",
      preprint: "UNPB",
      presentation: "GEN",
      radioBroadcast: "SOUND",
      report: "RPRT",
      standard: "STAND",
      statute: "STAT",
      thesis: "THES",
      tvBroadcast: "MPCT",
      videoRecording: "VIDEO",
    };

    const currentItem = localItemTypes.find((item) => item.key.toLowerCase() === currentItemType.toLowerCase());
    const noodleToolsType = currentItem?.noodleTools;

    lines.push(`TY  - ${risTypeMap[currentItemType.toLowerCase()] || risTypeMap[currentItemType] || "GEN"}`);

    if (noodleToolsType) {
      lines.push(`M3  - ${noodleToolsType}`);
    }

    // Title mapping (handle Case Name for cases)
    const title = articleData.title || articleData.caseName || articleData.firstName;
    if (title) lines.push(`TI  - ${title}`);

    // Author mapping
    if (authors?.length > 0) {
      authors.forEach((author) => {
        if (author.firstName || author.lastName) {
          lines.push(`AU  - ${author.lastName}, ${author.firstName}`);
        }
      });
    }

    const rawDate = articleData.date || articleData.dateDecided || articleData.issueDate || articleData.filingDate || articleData.dateEnacted;
    if (noDate || rawDate === "n.d.") {
      lines.push(`PY  - n.d.`);
      lines.push(`DA  - n.d.`);
    } else if (rawDate) {
      const isDayjs = rawDate && typeof rawDate === 'object' && typeof rawDate.format === 'function';
      const dateStr = isDayjs ? rawDate.format("YYYY-MM-DD") : String(rawDate).trim();

      const parts = parseCitationDateParts(dateStr);
      let year = parts.year;
      const month = parts.month;
      const day = parts.day;

      if (!year || !/^\d{4}$/.test(year)) {
        const yearMatch = dateStr.match(/\d{4}/);
        if (yearMatch) year = yearMatch[0];
      }

      if (year && /^\d{4}$/.test(year)) {
        lines.push(`PY  - ${year}`);

        const isStandardMonth = SINGLE_MONTH_OPTIONS.includes(month);
        const monthNum = isStandardMonth ? MONTH_NAME_TO_NUMBER[month] : "";
        const dayNum = (isStandardMonth && day) ? String(day).padStart(2, "0") : "";

        let daValue;
        if (isStandardMonth) {
          daValue = `${year}/${monthNum}/${dayNum}/`;
        } else if (month) {
          daValue = `${year}///${month}`;
        } else {
          daValue = `${year}///`;
        }

        lines.push(`DA  - ${daValue}`);
      } else if (dateStr && dateStr.toLowerCase().replace(/[\.\(\)\s]/g, "") !== "nd") {
        lines.push(`DA  - ${dateStr}`);
      } else {
        lines.push(`PY  - `);
        lines.push(`DA  - `);
      }
    } else {
      lines.push(`PY  - `);
      lines.push(`DA  - `);
    }

    // 5. Container Mapping (Ultra-Robust Source Name Detector)
    // This logic ensures that even if a user clears the UI fields, we recover the source name from original state.
    const findBestSource = () => {
      // Priority 1: User-edited fields
      const edited = articleData.publicationTitle || articleData.websiteTitle || articleData.blogTitle ||
        articleData.forumTitle || articleData.dictionaryTitle || articleData.encyclopediaTitle ||
        articleData.proceedingsTitle || articleData.programTitle;
      if (edited) return edited;

      // Priority 2: Original fetched metadata (immune to current UI clearing)
      const original = originalArticleData?.publicationTitle || originalArticleData?.websiteTitle ||
        originalArticleData?.blogTitle || originalArticleData?.journal ||
        originalArticleData?.source || originalArticleData?.name;
      if (original) return original;

      return "";
    };

    const effectiveContainerTitle = findBestSource();

    if (effectiveContainerTitle) {
      lines.push(`T2  - ${effectiveContainerTitle}`);
      const typeLower = currentItemType.toLowerCase();

      // Type-specific secondary mappings per user request
      if (typeLower === 'journalarticle') {
        lines.push(`JO  - ${effectiveContainerTitle}`);
      } else if (['magazinearticle', 'newspaperarticle'].includes(typeLower)) {
        lines.push(`JO  - ${effectiveContainerTitle}`);
        lines.push(`JF  - ${effectiveContainerTitle}`);
      } else if (typeLower === 'blogpost') {
      }
    }

    // Series info
    if (articleData.series) lines.push(`T3  - ${articleData.series}`);
    if (articleData.seriesNumber) lines.push(`M1  - Series No. ${articleData.seriesNumber}`);

    // 6. Volume/Issue/Pages
    if (articleData.volume) lines.push(`VL  - ${articleData.volume}`);
    if (articleData.reporterVolume) lines.push(`VL  - ${articleData.reporterVolume}`);
    if (articleData.issue) lines.push(`IS  - ${articleData.issue}`);
    if (articleData.pages) {
      const pageRange = String(articleData.pages);
      const pageParts = pageRange.split(/[-–—]/).map(p => p.trim());
      if (pageParts.length === 2 && pageParts[0] && pageParts[1]) {
        lines.push(`SP  - ${pageParts[0]}`);
        lines.push(`EP  - ${pageParts[1]}`);
      } else {
        lines.push(`SP  - ${pageRange}`);
      }
    }
    if (articleData.firstPage) lines.push(`SP  - ${articleData.firstPage}`);
    if (articleData.numPages && !articleData.pages) lines.push(`SP  - ${articleData.numPages}`);
    if (articleData.edition) lines.push(`ET  - ${articleData.edition}`);

    // 7. Legal/Governmental/Technical Specifics
    // PB explicitly includes "court" per RIS spec. CT means caption/title — wrong for court.
    // Docket number goes in N1 — ID tag is for internal DB reference IDs (max 20 chars).
    if (articleData.court) lines.push(`PB  - ${articleData.court}`);
    if (articleData.docketNumber) lines.push(`N1  - Docket No: ${articleData.docketNumber}`);
    if (articleData.reporter) lines.push(`JO  - ${articleData.reporter}`);
    if (articleData.legislativeBody) lines.push(`PB  - ${articleData.legislativeBody}`);
    if (articleData.session) lines.push(`M1  - Session: ${articleData.session}`);
    if (articleData.billNumber) lines.push(`M1  - Bill: ${articleData.billNumber}`);
    if (articleData.publicLawNumber) lines.push(`M1  - Public Law: ${articleData.publicLawNumber}`);
    if (articleData.patentNumber) lines.push(`M1  - Patent No: ${articleData.patentNumber}`);
    if (articleData.assignee) lines.push(`PB  - ${articleData.assignee}`);

    const mediumHint =
      articleData.artworkMedium ||
      articleData.format ||
      articleData.audioFileType ||
      articleData.videoRecordingFormat ||
      articleData.postType ||
      articleData.thesisType ||
      articleData.reportType ||
      articleData.presentationType ||
      articleData.genre;

    if (mediumHint) {
      if (noodleToolsType) {
        lines.push(`M1  - ${mediumHint}`);
      } else {
        lines.push(`M3  - ${mediumHint}`);
      }
    }
    if (articleData.runningTime) lines.push(`M1  - Run Time: ${articleData.runningTime}`);
    if (articleData.episodeNumber) lines.push(`M1  - Episode: ${articleData.episodeNumber}`);
    if (articleData.network) lines.push(`M1  - Network: ${articleData.network}`);

    // 9. URL and Access Date
    if (articleData.url) lines.push(`UR  - ${articleData.url}`);
    if (articleData.accessDate) {
      const ad = new Date(articleData.accessDate);
      if (!isNaN(ad.getTime())) {
        const year = ad.getFullYear();
        const month = String(ad.getMonth() + 1).padStart(2, '0');
        const day = String(ad.getDate()).padStart(2, '0');
        lines.push(`Y2  - ${year}/${month}/${day}`);
      }
    }

    // 10. Secondary Metadata (mapped strictly from citation.js)
    if (articleData.abstractNote) lines.push(`AB  - ${articleData.abstractNote}`);
    if (articleData.language) lines.push(`LA  - ${articleData.language}`);

    // Publisher mapping with fallback for Books, Webpages, etc.
    const publisher =
      articleData.publisher ||
      articleData.university ||
      articleData.institution ||
      articleData.company ||
      articleData.organization ||
      (['book', 'webpage', 'booksection'].includes(currentItemType.toLowerCase()) ? effectiveContainerTitle : null);

    if (publisher) {
      lines.push(`PB  - ${publisher}`);
    }

    if (articleData.place) lines.push(`CY  - ${articleData.place}`);
    if (articleData.ISBN) lines.push(`SN  - ${articleData.ISBN}`);
    if (articleData.ISSN) lines.push(`SN  - ${articleData.ISSN}`);
    if (articleData.DOI) lines.push(`DO  - ${articleData.DOI}`);
    if (articleData.shortTitle) lines.push(`ST  - ${articleData.shortTitle}`);
    if (articleData.extra) lines.push(`N1  - ${articleData.extra}`);
    if (articleData.notes) lines.push(`N1  - ${articleData.notes}`);
    if (articleData.archive) lines.push(`M1  - Archive: ${articleData.archive}`);
    if (articleData.archiveLocation) lines.push(`M1  - Location: ${articleData.archiveLocation}`);
    if (articleData.repository) lines.push(`DB  - ${articleData.repository}`);
    if (articleData.libraryCatalog) lines.push(`DP  - ${articleData.libraryCatalog}`);

    lines.push("ER  - ");
    return lines.join("\n");
  };

  const handleExportToNoodleTools = () => {
    const risString = buildRISData();
    // Set the textarea value directly on the DOM element before submitting
    if (formRef.current) {
      const textarea = formRef.current.querySelector(
        "textarea[name='risdatabyformpost']"
      );
      if (textarea) textarea.value = risString;
      formRef.current.submit();
    }
  };

  const handleExportToRefWorks = () => {
    const risString = buildRISData();
    if (refWorksFormRef.current) {
      const textarea = refWorksFormRef.current.querySelector(
        "textarea[name='ImportData']"
      );
      if (textarea) textarea.value = risString;
      refWorksFormRef.current.submit();
    }
  };

  const downloadFile = (data, filename, type) => {
    const blob = new Blob([data], { type });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadRIS = () => {
    downloadFile(
      buildRISData(),
      "citation.ris",
      "application/x-research-info-systems"
    );
  };

  const buildBibTeXData = () => {
    const entryType = currentItemType === "book" ? "book" : "misc";
    let bib = `@${entryType}{citation,\n`;
    if (articleData.title) bib += `  title = {${articleData.title}},\n`;
    if (authors?.length > 0) {
      const authorList = authors
        .filter((a) => a.firstName || a.lastName)
        .map((a) => `${a.lastName || ""}, ${a.firstName || ""}`)
        .join(" and ");
      if (authorList) bib += `  author = {${authorList}},\n`;
    }
    if (articleData.date) {
      if (isStructuredCitationDate(articleData.date)) {
        const d = new Date(articleData.date);
        bib += `  year = {${d.getFullYear()}},\n`;
      } else if (typeof articleData.date === "string" && articleData.date.trim()) {
        bib += `  year = {${articleData.date.trim()}},\n`;
      }
    }
    if (articleData.url) bib += `  url = {${articleData.url}},\n`;
    if (articleData.publication)
      bib += `  journal = {${articleData.publication}},\n`;
    bib += `}`;
    return bib;
  };

  const handleDownloadBibTeX = () => {
    downloadFile(buildBibTeXData(), "citation.bib", "application/x-bibtex");
  };



  const handleSelectStyleType = (e) => {
    if (e.target.value === selectedStyle) {
      setStyleChanged(false);
    } else {
      setStyleChanged(true);
    }
    setSelectedStyle(e.target.value);
  };

  const handleSelectItemType = (e) => {
    handleItemChange(e.target.value);
  };

  const CitationBox = () => {
    const missingHardFields = requiredFields.filter((field) => checkRequiredFields(field));
    const hasMissingFields = missingHardFields.length > 0;
    const missingSoftFields = (softRequirementFields || []).filter((field) => checkSoftRequiredFields(field));
    const hasMissingSoftFields = missingSoftFields.length > 0;

    return (
      <Box sx={{ mb: 2, zIndex: 1000 }}>
        {hasMissingFields ? (
          <Box p={1.5} sx={{ backgroundColor: "rgba(211, 47, 47, 0.1)", borderRadius: "4px", display: "flex", alignItems: "start", gap: 1 }}>
            <Typography variant="subtitle2" sx={{ color: "error.main", fontWeight: 600, flex: 1 }}>
              Your citation is missing required information. Please fill in all required fields.
            </Typography>
            <Tooltip
              arrow
              placement="bottom-start"
              title={
                <Box p={1}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#fff' }}>Missing Required Info:</Typography>
                  <Stack gap={0.5}>
                    {missingHardFields.map(field => (
                      <Typography key={field} variant="caption" sx={{ display: 'list-item', ml: 2, color: '#fff' }}>
                        {fields[field] || (field === 'creators' ? 'Author' : field)}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
              }
            >
              <InfoOutlinedIcon
                sx={{ color: "error.main", fontSize: "16px", cursor: "pointer", mt: 0.3 }}
              />
            </Tooltip>
          </Box>
        ) : (
          <>
            <Typography sx={{ mb: 0.5 }} variant="body3" color="grey.600">
              Generated Citation
            </Typography>
            <Typography
              variant="subtitle2"
              dangerouslySetInnerHTML={{ __html: citation }}
              sx={{
                maxHeight: "15vh",
                overflowY: "auto",
                overflowX: "hidden",
                p: 1.5,
                border: hasMissingSoftFields ? "1px solid" : "none",
                borderColor: "#f57c00",
                borderRadius: "4px",
                backgroundColor: hasMissingSoftFields ? "rgba(245, 124, 0, 0.05)" : "transparent",
                color: hasMissingSoftFields ? "#f57c00" : "inherit"
              }}
            />
            {hasMissingSoftFields && (
              <Box mt={1} p={1.5} sx={{ backgroundColor: "rgba(245, 124, 0, 0.1)", borderRadius: "4px", display: "flex", alignItems: "start", gap: 1 }}>
                <Typography variant="caption" sx={{ color: "#f57c00", fontWeight: 500 }}>
                  Wait! Your citation might not be complete. It is missing information that is <i>typically</i> included. Please double-check your source document and provide the missing info, if it is available.
                </Typography>
                <Tooltip
                  arrow
                  placement="bottom-start"
                  title={
                    <Box p={1}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#fff' }}>Missing Recommended Info:</Typography>
                      <Stack gap={0.5}>
                        {missingSoftFields.map(field => (
                          <Typography key={field} variant="caption" sx={{ display: 'list-item', ml: 2, color: '#fff' }}>
                            {fields[field] || (field === 'creators' ? 'Author' : field)}
                          </Typography>
                        ))}
                      </Stack>
                    </Box>
                  }
                >
                  <InfoOutlinedIcon
                    sx={{ color: "#f57c00", fontSize: "16px", cursor: "pointer", mt: 0.3 }}
                  />
                </Tooltip>
              </Box>
            )}
            <Stack direction="row" gap={2} mt={2}>
              <Tooltip title={tooltipText} arrow>
                <Button
                  startIcon={<ContentCopyIcon />}
                  sx={{ px: 2, py: 1 }}
                  variant="outlined"
                  onClick={handleCopyClick}
                  onMouseLeave={handleMouseLeave}
                >
                  Copy
                </Button>
              </Tooltip>
              {NOODLETOOLS_CLIENT_KEY && (
                <form
                  ref={formRef}
                  action="https://my.noodletools.com/import/rest-post.php"
                  target="_blank"
                  method="POST"
                >
                  <input
                    type="hidden"
                    name="accesskey"
                    value={NOODLETOOLS_ACCESS_KEY}
                  />
                  <input
                    type="hidden"
                    name="clientkey"
                    value={NOODLETOOLS_CLIENT_KEY}
                  />
                  <input type="hidden" name="version" value="1.0" />
                  <input type="hidden" name="datatype" value="ris" />
                  <textarea
                    name="risdatabyformpost"
                    defaultValue=""
                    readOnly
                    style={{ display: "none" }}
                  />
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={handleExportToNoodleTools}
                    disabled={hasMissingFields}
                    sx={{
                      px: 2,
                      py: 1,
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                  >
                    Export to NoodleTools
                  </Button>
                </form>
              )}
            </Stack>
            {NOODLETOOLS_CLIENT_KEY && (
              <Box mt={1} px={1}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block" }}
                >
                  Please ensure your citation is correct before you save, copy, or export it.
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>
    );
  };

  const renderFieldValue = (field) => {
    if (editingField === field) {
      return editingValue;
    } else
      switch (field) {
        case "date":
          return formatCitationDateValue(articleData[field]);
        case "accessDate":
          return getRegularDate(articleData[field]);
        default:
          return articleData[field];
      }
  };

  const handleBlur = (field) => {
    setEditingField(null);
    if (field === "creators" || field === "author") {
    } else {
      setArticleData((prevData) => ({
        ...prevData,
        [field]: editingValue,
      }));
    }
  };

  const handleInputChange = (value) => {
    setEditingValue(value);
  };

  const addAuthor = (position) => {
    const newAuthor = {
      firstName: "",
      lastName: "",
      creatorType: "author",
    };

    setAuthors([
      ...authors.slice(0, position),
      newAuthor,
      ...authors.slice(position),
    ]);
  };

  const removeAuthor = (index) => {
    const newAuthors = [...authors];
    newAuthors.splice(index, 1);
    setAuthors(newAuthors);
    setArticleData((prevData) => ({ ...prevData, creators: newAuthors }));
  };

  const handleAuthorChange = (index, field, value) => {
    const newAuthors = [...authors];
    newAuthors[index][field] = value;
    setAuthors(newAuthors);
  };

  const handleDateChange = (field, val) => {
    if (field === "date") {
      setDate(val);
    } else {
      setAccessDate(val);
    }
    setArticleData((prevData) => ({ ...prevData, [field]: val }));
  };

  const handleCitationYearChange = (e) => {
    setCitationYear(e.target.value);
    setCitationYearError(false);
  };

  const handleCitationYearBlur = ({ target }) => {
    const rawValue = target.value.trim();
    const normalizedYearValue = normalizeCitationYearValue(rawValue);

    const yearToSet = normalizedYearValue || rawValue;
    setCitationYear(yearToSet);

    // Manually trigger articleData update on blur to avoid API calls while typing
    setArticleData((prevData) => {
      const nextDateValue = buildCitationDateValue({
        day: citationDay,
        month: citationMonth,
        year: yearToSet,
      });
      if ((prevData?.date || "") === nextDateValue) {
        return prevData;
      }
      return { ...prevData, date: nextDateValue };
    });

    if (citationYearInputRef.current && citationYearInputRef.current.value !== yearToSet) {
      citationYearInputRef.current.value = yearToSet;
    }

    const isValid = isCitationYearValueValid(yearToSet);
    setCitationYearError(!isValid);
  };

  const handleAuthorBlur = () => { };

  const checkRequiredFields = (field) => {
    if (field === "creators") {
      return (
        requiredFields.includes(field) && !authors[0]?.firstName && !(editingField === "creators" && editingValue)
      );
    }
    if (field === "date") {
      if (noDate) return false;
      const hasDateValue = Boolean(citationDay || citationMonth || citationYear);
      return requiredFields.includes(field) && (!hasDateValue || isDateIncomplete);
    }
    if (field === "accessDate") {
      if (!requiredFields.includes(field)) return false;
      const val = articleData[field];
      if (!val) return true;
      if (typeof val === "object" && "isValid" in val) return !val.isValid();
      const d = new Date(val);
      return isNaN(d.getTime()) || d.getFullYear() <= 1970;
    }
    return (
      requiredFields.includes(field) && !articleData[field] && !(editingField === field && editingValue)
    );
  };

  const checkSoftRequiredFields = (field) => {
    if (field === "creators") {
      return (
        softRequirementFields.includes(field) && !authors[0]?.firstName && !(editingField === "creators" && editingValue)
      );
    }
    if (field === "date") {
      if (noDate) return false;
      const hasSomeDate = Boolean(citationDay || citationMonth || citationYear);
      return softRequirementFields.includes(field) && !hasSomeDate;
    }
    if (field === "accessDate") {
      if (!softRequirementFields.includes(field)) return false;
      const val = articleData[field];
      if (!val) return true;
      if (typeof val === "object" && "isValid" in val) return !val.isValid();
      const d = new Date(val);
      return isNaN(d.getTime()) || d.getFullYear() <= 1970;
    }
    return (
      softRequirementFields.includes(field) && !articleData[field] && !(editingField === field && editingValue)
    );
  };

  const RenderEditableField = (field) => {
    let required = checkRequiredFields(field);
    let softRequired = !required && checkSoftRequiredFields(field);
    if (field === "date" && (citationYearError || isDateIncomplete)) {
      required = true;
    }
    const commonSx = {
      mb: 2,
      ...(softRequired ? {
        '& .MuiOutlinedInput-root': {
          '& fieldset': {
            borderColor: '#f57c00',
          },
          '&:hover fieldset': {
            borderColor: '#f57c00',
          },
          '&.Mui-focused fieldset': {
            borderColor: '#f57c00',
          },
        },
        '& .MuiInputLabel-root': {
          color: '#f57c00',
        },
        '& .MuiInputLabel-root.Mui-focused': {
          color: '#f57c00',
        }
      } : {})
    };
    return field === "creators" ? (
      <Box key={field} tabIndex={-1} onBlur={handleAuthorBlur}>
        {authors.map((author, index) => (
          <Box mt={2} mb={2} key={`${field}-${index}`}>
            <Stack
              direction="row"
              alignItems="start"
              justifyContent="space-between"
              gap={2}
            >
              <TextField
                value={
                  editingField === "firstName" && index === authorIndex
                    ? editingValue
                    : author.firstName
                }
                onFocus={() => {
                  setAuthorIndex(index);
                  setEditingField("firstName");
                  setEditingValue(author.firstName);
                }}
                onBlur={(e) =>
                  handleAuthorChange(index, "firstName", editingValue)
                }
                onChange={(e) => handleInputChange(e.target.value)}
                label="First Name"
                sx={commonSx}
                error={required}
                helperText={required ? "This field is required" : ""}
              />
              <TextField
                value={
                  editingField === "lastName" && index === authorIndex
                    ? editingValue
                    : author.lastName
                }
                onFocus={() => {
                  setAuthorIndex(index);
                  setEditingField("lastName");
                  setEditingValue(author.lastName);
                }}
                onBlur={(e) =>
                  handleAuthorChange(index, "lastName", editingValue)
                }
                onChange={(e) => handleInputChange(e.target.value)}
                label="Last Name"
                sx={commonSx}
              />
              <AddIcon
                onClick={() => addAuthor(index + 1)}
                sx={{ cursor: "pointer", mt: 1 }}
              />
              <DeleteIcon
                onClick={() => {
                  if (index !== 0) {
                    removeAuthor(index);
                  }
                }}
                sx={{
                  cursor: index === 0 ? "default" : "pointer",
                  color: index === 0 ? "gray" : "inherit",
                  mt: 1,
                }}
              />
            </Stack>
          </Box>
        ))}
      </Box>
    ) : field === "accessDate" ? (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          sx={commonSx}
          label="Access Date"
          value={accessDate}
          onChange={(val) => handleDateChange(field, val)}
          slotProps={{
            textField: {
              error: required,
              helperText: required ? "This field is required" : "",
            },
          }}
        />
      </LocalizationProvider>
    ) : field === "date" ? (
      <Stack key={field} spacing={1} sx={commonSx}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography
            variant="body2"
            sx={{
              color: required ? "error.main" : softRequired ? "#f57c00" : "text.primary",
            }}
          >
            {fields[field]}
          </Typography>
          <FormControlLabel
            sx={{ mr: 0 }}
            control={
              <Checkbox
                id="no-date-checkbox"
                checked={noDate}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setNoDate(checked);
                  if (checked) {
                    setCitationDay("");
                    setCitationMonth("");
                    setCitationYear("");
                    setCitationYearError(false);
                  }
                }}
                size="small"
              />
            }
            label={<Typography variant="caption">No Date</Typography>}
          />
        </Stack>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          gap={2}
          sx={{ width: "100%", minWidth: 0 }}
        >
          <TextField
            select
            label={CITATION_DATE_LABELS.day}
            value={citationDay}
            onChange={(e) =>
              setCitationDay(e.target.value)
            }
            disabled={noDate || (citationMonth && !SINGLE_MONTH_OPTIONS.includes(citationMonth))}
            error={required}
            sx={{
              width: { xs: "100%", sm: "20%" },
              minWidth: 0,
              flex: { sm: 1 },
            }}
          >
            <MenuItem value="">None</MenuItem>
            {DD_OPTIONS.filter((opt) => opt !== "None" && opt !== "").map((dayOption) => (
              <MenuItem key={dayOption} value={dayOption}>
                {dayOption}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={CITATION_DATE_LABELS.month}
            value={citationMonth}
            onChange={(e) => {
              const val = e.target.value;
              setCitationMonth(val);
              if (val && !SINGLE_MONTH_OPTIONS.includes(val)) {
                setCitationDay("");
              }
            }}
            error={required}
            disabled={noDate}
            sx={{
              width: { xs: "100%", sm: "40%" },
              minWidth: 0,
              flex: { sm: 2 },
            }}
          >
            <MenuItem value="">None</MenuItem>
            {MM_OPTIONS.filter((opt) => opt !== "None" && opt !== "").map((monthOption) => (
              <MenuItem key={monthOption} value={monthOption}>
                {monthOption}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            inputRef={citationYearInputRef}
            label={CITATION_DATE_LABELS.year}
            placeholder="YYYY"
            value={citationYear}
            onChange={handleCitationYearChange}
            onBlur={handleCitationYearBlur}
            error={required || citationYearError}
            disabled={noDate}
            inputProps={{
              maxLength: CITATION_YEAR_MAX_LENGTH,
            }}
            sx={{
              width: { xs: "100%", sm: "40%" },
              minWidth: 0,
              flex: { sm: 2 },
            }}
          />
        </Stack>
        {(required || citationYearError) && (
          <Typography
            id="date-error-message"
            variant="caption"
            sx={{
              mt: 0.5,
              color: "error.main",
            }}
          >
            {citationYearError
              ? 'Invalid year. Enter a 4-digit year'
              : isDateIncomplete
                ? 'Invalid date. Enter DD / MM / YYYY, MM / YYYY, or just YYYY'
                : "This field is required"}
          </Typography>
        )}
      </Stack>
    ) : (
      <TextField
        key={field}
        value={editingField === field ? editingValue : renderFieldValue(field)}
        onFocus={() => {
          setEditingField(field);
          setEditingValue(renderFieldValue(field));
        }}
        onBlur={() => handleBlur(field)}
        label={fields[field]}
        sx={commonSx}
        error={required}
        helperText={required ? "This field is required" : ""}
        onChange={(e) => handleInputChange(e.target.value)}
      />
    );
  };

  useEffect(() => {
    if (document.activeElement === citationYearInputRef.current) {
      return;
    }

    const isDualMonth = citationMonth && !SINGLE_MONTH_OPTIONS.includes(citationMonth);

    setArticleData((prevData) => {
      const nextDateValue = buildCitationDateValue({
        day: isDualMonth ? "" : citationDay,
        month: citationMonth,
        year: citationYear,
        noDate,
      });

      if ((prevData?.date || "") === nextDateValue) {
        return prevData;
      }

      return {
        ...prevData,
        date: nextDateValue,
      };
    });
  }, [citationDay, citationMonth, citationYear, noDate, setArticleData]);

  useEffect(() => {
    const parsedDateParts = parseCitationDateParts(articleData?.date);
    const currentDateValue = buildCitationDateValue({
      day: citationDay,
      month: citationMonth,
      year: citationYear,
      noDate,
    });

    if ((articleData?.date || "") === currentDateValue) {
      return;
    }

    const isYearEssentiallyEmpty = !parsedDateParts.year;
    const isCurrentYearNonStandard = citationYear === CITATION_YEAR_ND_VALUE || (citationYear && !/^\d{4}$/.test(citationYear));

    if (
      parsedDateParts.day !== citationDay ||
      parsedDateParts.month !== citationMonth ||
      (parsedDateParts.year !== citationYear && !(isYearEssentiallyEmpty && isCurrentYearNonStandard))
    ) {
      setCitationDay(parsedDateParts.day);
      setCitationMonth(parsedDateParts.month);
      setNoDate(parsedDateParts.year === "n.d.");
      // Skip updating year if user is currently typing in it
      if (document.activeElement !== citationYearInputRef.current) {
        setCitationYear(parsedDateParts.year);
      }
      setCitationYearError(false);
    }
  }, [articleData?.date]);

  useEffect(() => {
    if (articleData) {
      const articleDataChanged =
        JSON.stringify(articleData) !== JSON.stringify(originalArticleData);
      const authorsChanged =
        JSON.stringify(authors) !== JSON.stringify(originalAuthors);
      const isDataModified = articleDataChanged || authorsChanged || styleChanged;

      setDisable(!isDataModified || citationYearError || isDateIncomplete);

      if (selectedBookmarkOption !== "citation") {
        return;
      }

      if (!citationLoaded) {
        return;
      }

      if (!hasHydratedInitialCitationRef.current) {
        hasHydratedInitialCitationRef.current = true;
        if (isCitationAvailable || citation) {
          return;
        }
      }

      if (!isDataModified) {
        return;
      }

      // A teacher/read-only viewer shouldn't cause a citation to be generated
      // for a bookmark the student never generated one for.
      if (isReadOnlyView && !isCitationAvailable) {
        return;
      }

      fetchCitation(fields, isDataModified, true);
    }
  }, [selectedBookmarkOption, articleData, authors, selectedStyle, citationYearError, isDateIncomplete, citationLoaded, isCitationAvailable, citation, isReadOnlyView]);

  if (selectedBookmarkOption !== "citation") {
    return <></>;
  }
  if (loading) {
    return (
      <Stack direction="column" gap={2} p={2}>
        <Skeleton variant="rectangular" height={118} />
        <Skeleton variant="rectangular" height={50} />
        <Skeleton variant="rectangular" height={50} />
        <Skeleton variant="rectangular" height={50} />
        <Skeleton variant="rectangular" height={50} />{" "}
      </Stack>
    );
  }

  if (isReadOnlyView && !isCitationAvailable && !citation) {
    return (
      <Stack gap={2} p={4}>
        <Typography variant="body2" color="text.secondary">
          This student hasn't generated a citation for this bookmark.
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack gap={2} p={4}>
      <CitationBox />

      <TextField
        select
        label="Style Type"
        value={selectedStyle}
        onChange={handleSelectStyleType}
        fullWidth
        variant="outlined"
        sx={{ mb: 2 }}
      >
        {citationStyles?.map((style) => (
          <MenuItem key={style["key"]} value={style["key"]}>
            {style["name"]}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        label="Item Type"
        value={currentItemType}
        onChange={handleSelectItemType}
        fullWidth
        variant="outlined"
        sx={{ mb: 2 }}
      >
        {itemTypes.map((style) => (
          <MenuItem key={style["key"]} value={style["key"]}>
            {style["name"]}
          </MenuItem>
        ))}
      </TextField>
      {Object.keys(fields).map((field) => RenderEditableField(field))}


    </Stack>
  );
};

export default Citation;
